import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CREATOR_USERNAME_COLUMN,
  type CreatorCohortImportPreviewRow,
  type CreatorCohortImportResult,
  type CreatorTagColor,
} from "@unbox/api";
import { ChevronDown, DotsGrid, Download01, FlipBackward, Plus, SearchMd, Trash01, Upload01 } from "@untitledui/icons";
import { useRef, useState } from "react";
import { useCreatorCohorts, useCreatorCohortsImportPreview, useCreatorCohortsImportSave } from "@/api/creator-cohorts";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { ImportCSV, type SaveResult } from "@/components/ImportCSV";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCurrency } from "@/hooks/use-currency";
import { formatConditionSummary } from "@/lib/creator-cohorts/condition-summary";
import { randomTagColor } from "@/lib/creator-tags/colors";
import { TagColorNameInput } from "@/pages/creator-tags/tag-color-name-input";
import { cx } from "@/utils/cx";
import { pluralize } from "@/utils/text";
import type { CohortLibraryEditor as CohortLibraryEditorState, DraftRow } from "./use-cohort-library-editor";

const IMPORT_COLUMNS = [CREATOR_USERNAME_COLUMN, "cohort"];

const cohortKey = (name: string) => name.trim().toLowerCase();

const ruleBand = (conditions: DraftRow["ruleConditions"], currency: string): string | null => {
  const summary = formatConditionSummary(conditions[0], currency);
  return summary ? [summary.value, summary.unit].filter(Boolean).join(" ") : null;
};

const CohortRow = ({
  row,
  pending,
  band,
  draggable,
  onUpdate,
  onToggleRemoved,
}: {
  row: DraftRow;
  pending: boolean;
  band: string | null;
  draggable: boolean;
  onUpdate: (patch: Partial<DraftRow>) => void;
  onToggleRemoved: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !draggable,
  });

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cx(pending && "bg-utility-error-50", isDragging && "relative z-10 opacity-60 shadow-lg")}
    >
      <TableCell className="w-8 pr-0">
        <button
          type="button"
          aria-label={`Reorder ${row.name || "new cohort"}`}
          disabled={!draggable}
          className="cursor-grab text-quaternary transition-colors hover:text-secondary active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-30"
          {...attributes}
          {...listeners}
        >
          <DotsGrid className="size-4" />
        </button>
      </TableCell>

      <TableCell>
        <div className={pending ? "opacity-45" : undefined}>
          <TagColorNameInput
            color={row.color}
            onColorChange={(color) => onUpdate({ color })}
            name={row.name}
            onNameChange={(name) => onUpdate({ name })}
            label="Cohort name"
            fullWidth
          />
        </div>
      </TableCell>

      {pending ? (
        <TableCell colSpan={4}>
          <div className="flex items-center justify-end whitespace-nowrap text-sm text-utility-error-700">
            {row.assignedCount > 0
              ? `Deleting this cohort will remove it from ${pluralize(row.assignedCount, "creator", "creators")}.`
              : "Marked for deletion."}
          </div>
        </TableCell>
      ) : (
        <>
          <TableCell className="whitespace-nowrap text-secondary">{band ?? "—"}</TableCell>
          <TableCell className="whitespace-nowrap text-right tabular-nums text-secondary">
            {row.assignedCount}
          </TableCell>
          <TableCell className="whitespace-nowrap text-secondary">{row.ownerName || "—"}</TableCell>
          <TableCell className="whitespace-nowrap capitalize text-secondary">{row.source}</TableCell>
        </>
      )}

      <TableCell>
        <Button
          color="tertiary"
          size="sm"
          iconLeading={pending ? FlipBackward : Trash01}
          aria-label={pending ? `Restore ${row.name}` : `Delete ${row.name}`}
          onClick={onToggleRemoved}
        />
      </TableCell>
    </TableRow>
  );
};

/**
 * The cohort-library editing UI (controls + table + CSV import) behind Manage All
 * Cohorts. Every row is editable at once and deletes are staged until Save. All
 * state lives in the `useCohortLibraryEditor` hook passed in via `editor`; this
 * component is the presentation only.
 */
export const CohortLibraryEditor = ({ editor }: { editor: CohortLibraryEditorState }) => {
  const {
    draft,
    visibleRows,
    search,
    setSearch,
    isRemoved,
    toggleRemoved,
    scrollContainerRef,
    updateRow,
    moveRow,
    addNewCohort,
    markStale,
    handleDownloadTemplate,
  } = editor;

  const { mutateAsync: previewCsv } = useCreatorCohortsImportPreview();
  const { mutateAsync: saveCsv } = useCreatorCohortsImportSave();
  const { data: serverCohorts } = useCreatorCohorts();
  const currency = useCurrency();

  const [nameEdits, setNameEdits] = useState<Record<string, string>>({});
  const [colorEdits, setColorEdits] = useState<Record<string, CreatorTagColor>>({});
  const newCohortColors = useRef<Record<string, CreatorTagColor>>({});

  const existingColors = new Map(
    (serverCohorts ?? []).map((cohort) => [cohortKey(cohort.name), cohort.color as CreatorTagColor]),
  );

  const cohortNameFor = (row: CreatorCohortImportPreviewRow) => nameEdits[row.handle] ?? row.cohortName ?? "";

  const cohortColorFor = (row: CreatorCohortImportPreviewRow): CreatorTagColor => {
    const key = cohortKey(cohortNameFor(row));
    const color = colorEdits[key] ?? existingColors.get(key) ?? newCohortColors.current[key];
    if (color) return color;
    newCohortColors.current[key] = randomTagColor();
    return newCohortColors.current[key];
  };

  const renderCohortPicker = (row: CreatorCohortImportPreviewRow) => {
    if (!row.cohortName) return null;
    return (
      <TagColorNameInput
        color={cohortColorFor(row)}
        name={cohortNameFor(row)}
        onColorChange={(color) => setColorEdits((prev) => ({ ...prev, [cohortKey(cohortNameFor(row))]: color }))}
        onNameChange={(name) => setNameEdits((prev) => ({ ...prev, [row.handle]: name }))}
        label="Cohort name"
      />
    );
  };

  const cohortEditsFor = (rows: CreatorCohortImportPreviewRow[]) => {
    const edits: Record<string, { name: string; color: CreatorTagColor }> = {};
    for (const row of rows) {
      if (!row.cohortName) continue;
      const key = cohortKey(cohortNameFor(row));
      const isCustomized = row.handle in nameEdits || key in colorEdits || !existingColors.has(key);
      if (isCustomized) edits[row.handle] = { name: cohortNameFor(row), color: cohortColorFor(row) };
    }
    return edits;
  };

  // A filtered list hides the rows a drop would land between, so ranking is only
  // offered on the whole list.
  const isSearching = search.trim() !== "";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) moveRow(String(active.id), String(over.id));
  };

  const handlePreview = async (file: File): Promise<CreatorCohortImportPreviewRow[]> => {
    const rows = await previewCsv(await file.text());
    setNameEdits({});
    setColorEdits({});
    newCohortColors.current = {};
    return rows;
  };

  const handleSave = async (
    selectedRows: CreatorCohortImportPreviewRow[],
    file: File,
  ): Promise<SaveResult<CreatorCohortImportResult>> => {
    const result = await saveCsv({ csv: await file.text(), cohortEdits: cohortEditsFor(selectedRows) });
    // The mutation already invalidates the cohort list query; mark the editor's
    // local draft stale so it re-seeds from that refetch instead of staying frozen
    // at the snapshot taken when the modal first opened.
    markStale();
    return { async: false, ...result };
  };

  return (
    <>
      {/* Header controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[14rem] flex-1">
          <Input
            icon={SearchMd}
            placeholder={`Search between ${pluralize(draft.length, "cohort", "cohorts")}…`}
            inputClassName="text-sm"
            value={search}
            onChange={setSearch}
            onClear={() => setSearch("")}
          />
        </div>

        <ImportCSV<CreatorCohortImportPreviewRow, CreatorCohortImportResult>
          renderRowExtra={renderCohortPicker}
          columns={IMPORT_COLUMNS}
          onDownloadTemplate={handleDownloadTemplate}
          onPreview={handlePreview}
          onSave={handleSave}
          getSuccessMessage={(result) =>
            `Assigned cohorts to ${result.creators} creators. ${result.cohortsCreated} cohorts created.`
          }
          renderTrigger={(openFilePicker, isPreviewing) => (
            <Dropdown.Root>
              <Button color="tertiary" iconTrailing={ChevronDown} isLoading={isPreviewing}>
                CSV Actions
              </Button>
              <Dropdown.Popover>
                <Dropdown.Menu>
                  <Dropdown.Item icon={Upload01} onClick={openFilePicker}>
                    Import CSV
                  </Dropdown.Item>
                  <Dropdown.Item icon={Download01} onClick={handleDownloadTemplate}>
                    Download template
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          )}
        />

        <Button color="secondary" iconLeading={Plus} onClick={addNewCohort}>
          New Cohort
        </Button>
      </div>

      {/* Table */}
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-px" />
                <TableHead className="w-full">Cohort</TableHead>
                <TableHead className="w-px whitespace-nowrap">Rule</TableHead>
                <TableHead className="w-px whitespace-nowrap text-right">Creators Assigned</TableHead>
                <TableHead className="w-px whitespace-nowrap">Owner</TableHead>
                <TableHead className="w-px whitespace-nowrap">Source</TableHead>
                <TableHead className="w-px" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-tertiary">
                    {draft.length === 0
                      ? "No cohorts yet. Create one to get started."
                      : "No cohorts match your search."}
                  </TableCell>
                </TableRow>
              ) : (
                <SortableContext items={visibleRows.map((row) => row.id)} strategy={verticalListSortingStrategy}>
                  {visibleRows.map((row) => (
                    <CohortRow
                      key={row.id}
                      row={row}
                      pending={isRemoved(row.id)}
                      band={ruleBand(row.ruleConditions, currency)}
                      draggable={isSearching === false}
                      onUpdate={(patch) => updateRow(row.id, patch)}
                      onToggleRemoved={() => toggleRemoved(row.id)}
                    />
                  ))}
                </SortableContext>
              )}
            </TableBody>
          </Table>
        </DndContext>
      </div>
    </>
  );
};
